import React, { useEffect } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { useIntl } from 'react-intl'

import { domainLocales } from 'domainLocales'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { Article, Category } from 'interfaces/types'

import {
  StyledCategoryExplainerWrapper,
  StyledCategoryExplainerMobile,
  StyledCategoryExplainerDesktop,
} from './index.styled'
import { ArticleCardWrapper } from '../articleCardWrapper/index'

type Props = {
  isMobile: boolean
  explainerPosts: Array<Article>
  categoryName: string
  categoryTextColor: string
  currentCategory?: Category
}

type PropsChildren = {
  explainerPosts: Array<Article>
  categoryName: string
  categoryTextColor: string
  currentCategory?: Category
}

const RenderDesktop = ({
  explainerPosts = [],
  categoryName,
  categoryTextColor,
  currentCategory,
}: PropsChildren) => {
  const { locale } = useRouterExtend()

  const firstItem: Article = explainerPosts[0]
  let anotherItem: Array<Article>

  if (explainerPosts.length >= 5) {
    anotherItem = explainerPosts.slice(1, 5)
  } else {
    explainerPosts[1] ? (anotherItem = [explainerPosts[1]]) : (anotherItem = [])
  }

  const handleClickSameCategory = (e: React.MouseEvent) => {
    e.preventDefault()
  }

  return (
    <StyledCategoryExplainerDesktop>
      {firstItem ? (
        <div className="first-section">
          <ArticleCardWrapper
            dataEventCategoryForTitle="Top Article Title"
            dataEventActionForTitle="Click 1"
            dataEventLabelForTitle={`https://${domainLocales[locale]}${firstItem.permalinkDecode}`}
            dataEventCategoryForBanner="Top Article Banner"
            dataEventActionForBanner="Click 1"
            dataEventLabelForBanner={`https://${domainLocales[locale]}${firstItem.permalinkDecode}`}
            dataEventCategoryForCategory="Top Category Title"
            dataEventActionForCategory="Click 1"
            dataEventLabelForCategory={`https://${domainLocales[locale]}${firstItem.category.permalinkDecode}`}
            article={firstItem}
            className="article-card no-replace-click"
            type="author"
            size="lg"
            direction="vertical"
            categoryName={categoryName}
            styleLabel={{ color: categoryTextColor }}
            categoryLink={
              currentCategory &&
              currentCategory.permalink === firstItem.category.permalink
                ? firstItem.permalink
                : firstItem.category.permalink
            }
            onClickCategoryName={handleClickSameCategory}
            bannerImageWrapProps={{
              sizes: '(max-width: 1180px) 48vw, 553px',
            }}
          />
        </div>
      ) : (
        explainerPosts.map((explainer, i) => (
          <ArticleCardWrapper
            dataEventCategoryForTitle="Top Article Title"
            dataEventActionForTitle={`Click ${i + 2}`}
            dataEventLabelForTitle={`https://${domainLocales[locale]}${explainer.permalinkDecode}`}
            dataEventCategoryForBanner="Top Article Banner"
            dataEventActionForBanner={`Click ${i + 2}`}
            dataEventLabelForBanner={`https://${domainLocales[locale]}${explainer.permalinkDecode}`}
            dataEventCategoryForCategory="Top Category Title"
            dataEventActionForCategory={`Click ${i + 2}`}
            dataEventLabelForCategory={`https://${domainLocales[locale]}${explainer.category.permalinkDecode}`}
            article={explainer}
            categoryName={categoryName}
            key={`explainer-${i}`}
            className="article-card no-replace-click"
            type="author"
            size="lg"
            direction="vertical"
            styleLabel={{ color: categoryTextColor }}
            categoryLink={
              currentCategory &&
              currentCategory.permalink === explainer.category.permalink
                ? explainer.permalink
                : explainer.category.permalink
            }
            onClickCategoryName={handleClickSameCategory}
          />
        ))
      )}
      <div
        className="second-section"
        data-type={explainerPosts.length >= 5 ? 'many-item' : 'one-item'}
      >
        {anotherItem.length > 0 &&
          anotherItem.map((post: Article, index: number) => (
            <ArticleCardWrapper
              dataEventCategoryForTitle="Top Article Title"
              dataEventActionForTitle={`Click ${index + 2}`}
              dataEventLabelForTitle={`https://${domainLocales[locale]}${post.permalinkDecode}`}
              dataEventCategoryForBanner="Top Article Banner"
              dataEventActionForBanner={`Click ${index + 2}`}
              dataEventLabelForBanner={`https://${domainLocales[locale]}${post.permalinkDecode}`}
              dataEventCategoryForCategory="Top Category Title"
              dataEventActionForCategory={`Click ${index + 2}`}
              dataEventLabelForCategory={`https://${domainLocales[locale]}${post.category.permalinkDecode}`}
              article={post}
              direction="vertical"
              className="article-card no-replace-click"
              key={index}
              type="author"
              size={explainerPosts.length >= 5 ? 'sm' : 'lg'}
              categoryName={categoryName}
              styleLabel={{ color: categoryTextColor }}
              text=""
              categoryLink={
                currentCategory &&
                currentCategory.permalink === post.category.permalink
                  ? post.permalink
                  : post.category.permalink
              }
              onClickCategoryName={handleClickSameCategory}
            />
          ))}
      </div>
    </StyledCategoryExplainerDesktop>
  )
}

const RenderMobile = ({
  explainerPosts = [],
  categoryName,
  categoryTextColor,
  currentCategory,
}: PropsChildren) => {
  const { locale } = useRouterExtend()

  const handleClickSameCategory = (e: React.MouseEvent) => {
    e.preventDefault()
  }

  useEffect(() => {
    document.querySelector('#test')?.scrollTo(0, 0)
  }, [explainerPosts])

  return (
    <StyledCategoryExplainerMobile id="test">
      {explainerPosts.map((explainer, i) => (
        <ArticleCardWrapper
          dataEventCategoryForTitle="Top Article Title"
          dataEventActionForTitle={`Click ${i + 1}`}
          dataEventLabelForTitle={`https://${domainLocales[locale]}${explainer.permalinkDecode}`}
          dataEventCategoryForBanner="Top Article Banner"
          dataEventActionForBanner={`Click ${i + 1} `}
          dataEventLabelForBanner={`https://${domainLocales[locale]}${explainer.permalinkDecode}`}
          dataEventCategoryForCategory="Top Category Title"
          dataEventActionForCategory={`Click ${i + 1} `}
          dataEventLabelForCategory={`https://${domainLocales[locale]}${explainer.category.permalinkDecode}`}
          article={explainer}
          style={
            i === explainerPosts.length - 1 ? { paddingRight: '16px' } : {}
          }
          categoryName={categoryName}
          key={`explainer-${explainer.id}`}
          className={
            explainerPosts.length === 1
              ? 'article-card-one-item no-replace-click'
              : 'article-card no-replace-click'
          }
          type="author"
          size="sm"
          direction="vertical"
          styleLabel={{ color: categoryTextColor }}
          text=""
          categoryLink={
            currentCategory &&
            currentCategory.permalink === explainer.category.permalink
              ? explainer.permalink
              : explainer.category.permalink
          }
          onClickCategoryName={handleClickSameCategory}
          bannerImageWrapProps={{
            sizes: '76vw',
          }}
        />
      ))}
    </StyledCategoryExplainerMobile>
  )
}

export const CategoryExplainer = ({
  isMobile,
  explainerPosts = [],
  categoryName,
  categoryTextColor,
  currentCategory,
}: Props) => {
  const { formatMessage } = useIntl()

  return (
    <StyledCategoryExplainerWrapper data-type-mobile={isMobile}>
      <Heading tag="h3" style={{ marginTop: '35px' }}>
        {formatMessage({ id: 'categoryPage.explainer' })}
      </Heading>

      {!isMobile ? (
        <RenderDesktop
          explainerPosts={explainerPosts}
          categoryName={categoryName}
          categoryTextColor={categoryTextColor}
          currentCategory={currentCategory}
        />
      ) : (
        <RenderMobile
          explainerPosts={explainerPosts}
          categoryName={categoryName}
          categoryTextColor={categoryTextColor}
          currentCategory={currentCategory}
        />
      )}
    </StyledCategoryExplainerWrapper>
  )
}
